import { useState, useEffect } from "react";
import { Upload, RefreshCw, Truck } from "lucide-react";
import api from "../../lib/api";
import { LENGTH_UNITS, toCm, fromCm, roundN } from "../../lib/units";
import { uid } from "../../lib/uid";

const blankLrItem = { description: "", quantity: 1, unit: "Nos", dim_unit: "cm", length_cm: 0, width_cm: 0, height_cm: 0, weight_kg: 0, notes: "" };

// Volumetric weight (kg) for a single unit = (L × W × H in cm) / 5000. UI info only — never printed on the LR.
const VOL_DIVISOR = 5000;
const volPerUnit = (it) => roundN((Number(it.length_cm || 0) * Number(it.width_cm || 0) * Number(it.height_cm || 0)) / VOL_DIVISOR, 2);

export function CargoSection({ form, set, setForm, vehicles, drivers, brokers = [], subs = [], isDelivered, onPodFile }) {
  const st = form.sub_transporter || {};
  const selectedSub = subs.find((s) => s.id === st.id);
  const selectedHub = (selectedSub?.hubs || []).find((h) => h.hub_name === st.hub_name);
  const setSub = (upd) => setForm((f) => ({ ...f, sub_transporter: { ...(f.sub_transporter || {}), ...upd } }));

  const items = form.items || [];
  const totalPkgs = Math.round(items.reduce((s, it) => s + Number(it.quantity || 0), 0));
  const totalWeight = roundN(items.reduce((s, it) => s + Number(it.weight_kg || 0), 0), 2);
  const totalVol = roundN(items.reduce((s, it) => s + volPerUnit(it) * Number(it.quantity || 0), 0), 2);

  // Keep the LR's package count + total weight derived from the item lines (source of truth).
  useEffect(() => {
    if (!items.length) return;
    setForm((f) => (f.no_of_packages === totalPkgs && f.weight_kg === totalWeight ? f : { ...f, no_of_packages: totalPkgs, weight_kg: totalWeight }));
  }, [totalPkgs, totalWeight, items.length, setForm]);


  const [dir, setDir] = useState(null);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirOpen, setDirOpen] = useState(false);

  async function loadDirectory() {
    setDirOpen(true);
    setDirLoading(true);
    try {
      const r = await api.get("/brokers/directory", { params: { from_location: form.from_location || "", to_location: form.to_location || "" } });
      setDir(r.data || []);
    } catch { setDir([]); }
    setDirLoading(false);
  }

  function applyRoute(broker, route) {
    const arr = (form.vehicles || []).slice();
    let idx = arr.findIndex((v) => !(v.broker_name || "").trim() && !(v.vehicle_type || "").trim() && !(v.vehicle_number || "").trim());
    if (idx === -1) { arr.push({ vehicle_number: "", driver_name: "", driver_phone: "", broker_id: "", broker_name: "", lh_advance: 0, lh_balance: 0, _uid: uid() }); idx = arr.length - 1; }
    const cur = { ...arr[idx] };
    cur.broker_id = broker.id || cur.broker_id;
    cur.broker_name = broker.name || cur.broker_name;
    if (route?.vehicle_type && !(cur.vehicle_type || "").trim()) cur.vehicle_type = route.vehicle_type;
    if (route?.dimensions && !(cur.dimensions || "").trim()) cur.dimensions = route.dimensions;
    arr[idx] = cur;
    set("vehicles", arr);
  }

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="section-title mb-0">Items / Cargo Lines</div>
          <button type="button" className="btn-ghost text-xs" onClick={() => set("items", [...(form.items || []), { ...blankLrItem, _uid: uid() }])} data-testid="lr-add-item">+ Add Item</button>
        </div>
        <div className="border border-[#E5E3DB] overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="table-th">Description</th>
                <th className="table-th w-20">Qty</th>
                <th className="table-th w-20">Unit</th>
                <th className="table-th w-16">Dim Unit</th>
                <th className="table-th w-16">L</th>
                <th className="table-th w-16">B</th>
                <th className="table-th w-16">H</th>
                <th className="table-th w-20">Wt kg</th>
                <th className="table-th w-20">Vol wt</th>
                <th className="table-th w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(form.items || []).length === 0 ? (
                <tr><td className="table-td text-[#68665E] text-center" colSpan={10}>No items. Click "+ Add Item" for boxes / plates / pipes / bundles, etc.</td></tr>
              ) : (form.items || []).map((it, i) => {
                const upd = (k, v) => { const items = form.items.slice(); items[i] = { ...items[i], [k]: v }; set("items", items); };
                const du = it.dim_unit || "cm";
                return (
                  <tr key={it._uid ?? i} className="border-b border-[#E5E3DB] last:border-0">
                    <td className="p-1"><input className="input-field border-0" value={it.description} onChange={(e)=>upd("description", e.target.value)} placeholder="Boxes / Plates / Pipes..." data-testid={`lr-item-desc-${i}`} /></td>
                    <td className="p-1"><input type="number" step="0.01" className="input-field border-0" value={it.quantity} onChange={(e)=>upd("quantity", Number(e.target.value))} /></td>
                    <td className="p-1">
                      <select className="input-field border-0" value={it.unit} onChange={(e)=>upd("unit", e.target.value)}>
                        <option>Nos</option><option>Boxes</option><option>Bundles</option><option>Pcs</option><option>Kg</option><option>MT</option><option>Meters</option><option>Ft</option>
                      </select>
                    </td>
                    <td className="p-1">
                      <select className="input-field border-0" value={du} onChange={(e)=>upd("dim_unit", e.target.value)} data-testid={`lr-item-dimunit-${i}`}>
                        {LENGTH_UNITS.map((u)=><option key={u} value={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="p-1"><input type="number" step="0.01" className="input-field border-0" value={roundN(fromCm(it.length_cm, du))} onChange={(e)=>upd("length_cm", toCm(Number(e.target.value), du))} data-testid={`lr-item-l-${i}`} /></td>
                    <td className="p-1"><input type="number" step="0.01" className="input-field border-0" value={roundN(fromCm(it.width_cm, du))} onChange={(e)=>upd("width_cm", toCm(Number(e.target.value), du))} data-testid={`lr-item-b-${i}`} /></td>
                    <td className="p-1"><input type="number" step="0.01" className="input-field border-0" value={roundN(fromCm(it.height_cm, du))} onChange={(e)=>upd("height_cm", toCm(Number(e.target.value), du))} data-testid={`lr-item-h-${i}`} /></td>
                    <td className="p-1"><input type="number" step="0.01" className="input-field border-0" value={it.weight_kg} onChange={(e)=>upd("weight_kg", Number(e.target.value))} /></td>
                    <td className="p-1 text-right text-[#68665E] tabular-nums" data-testid={`lr-item-vol-${i}`}>{roundN(volPerUnit(it) * Number(it.quantity || 0), 2) || "—"}</td>
                    <td className="p-1 text-center"><button type="button" onClick={()=>set("items", form.items.filter((_,idx)=>idx!==i))} className="text-[#8A2526]">×</button></td>
                  </tr>
                );
              })}
            </tbody>
            {(form.items || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#E5E3DB] bg-[#FAF9F5] font-semibold">
                  <td className="table-td text-right" colSpan={1}>Totals</td>
                  <td className="table-td text-center" data-testid="lr-total-packages">{totalPkgs}</td>
                  <td className="table-td text-[10px] text-[#68665E] font-normal" colSpan={5}>packages (auto from items)</td>
                  <td className="table-td text-right" data-testid="lr-total-weight">{totalWeight}</td>
                  <td className="table-td text-right" data-testid="lr-total-vol">{totalVol}</td>
                  <td className="table-td"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="text-[11px] text-[#68665E] mt-2">Dimensions are optional. Enter them in any unit (Dim Unit dropdown) — they are always <strong>shown in feet (ft)</strong> on the LR detail page and the printed consignment note. <strong>Total packages &amp; weight</strong> are added up from the item lines automatically. <strong>Volumetric weight</strong> (L×B×H ÷ {VOL_DIVISOR}) is shown for your reference only — it is <strong>not printed</strong> on the LR.</div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="section-title mb-0">Vehicles &amp; Drivers</div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost text-xs flex items-center gap-1" onClick={loadDirectory} data-testid="lr-reuse-broker-route">
              <RefreshCw className="w-3.5 h-3.5" /> Reuse broker/route
            </button>
            <button type="button" className="btn-ghost text-xs" onClick={() => set("vehicles", [...(form.vehicles || []), { vehicle_number: "", driver_name: "", driver_phone: "", broker_id: "", broker_name: "", lh_advance: 0, lh_balance: 0, _uid: uid() }])} data-testid="lr-add-vehicle">+ Add Vehicle</button>
          </div>
        </div>
        {dirOpen && (
          <div className="border border-[#E5E3DB] bg-[#FAF9F5] p-3 mb-3" data-testid="lr-broker-route-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-[#1A1A18]">
                Saved brokers {(form.from_location || form.to_location) ? <>for <span className="text-[#C84B31]">{form.from_location || "?"} → {form.to_location || "?"}</span></> : "(all routes)"}
              </div>
              <button type="button" className="text-[#8A2526] text-xs" onClick={()=>setDirOpen(false)} data-testid="lr-broker-route-close">Close</button>
            </div>
            {dirLoading ? <div className="text-xs text-[#68665E]">Loading…</div>
              : !(dir && dir.length) ? <div className="text-xs text-[#68665E]" data-testid="lr-broker-route-empty">No saved brokers/routes {(form.from_location || form.to_location) ? "for this route" : "yet"}. They build up automatically each time you create an LR with a broker + route.</div>
              : (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {dir.map((b) => (
                    <div key={b.id} className="border border-[#E5E3DB] bg-white p-2" data-testid={`lr-broker-${b.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Truck className="w-3.5 h-3.5 text-[#C84B31]" />
                        <span className="text-xs font-semibold">{b.name}</span>
                        {b.phone && <span className="text-[11px] text-[#68665E]">· {b.phone}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(b.routes && b.routes.length ? b.routes : [{}]).map((r, ri) => (
                          <button type="button" key={ri} onClick={()=>{ applyRoute(b, r); setDirOpen(false); }} className="text-[11px] border border-[#E5E3DB] hover:border-[#C84B31] hover:text-[#C84B31] px-2 py-1 bg-[#FAF9F5]" data-testid={`lr-broker-route-use-${b.id}-${ri}`}>
                            {(r.from_location || r.to_location) ? `${r.from_location || "?"} → ${r.to_location || "?"}` : "Use this broker"}{r.vehicle_type ? ` · ${r.vehicle_type}` : ""}{r.dimensions ? ` · ${r.dimensions}` : ""}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
        <datalist id="vehicle-list">{vehicles.map((v)=><option key={v.id} value={v.vehicle_number} />)}</datalist>
        <datalist id="driver-list">{drivers.map((d)=><option key={d.id} value={d.name} />)}</datalist>
        <datalist id="broker-list">{brokers.map((b)=><option key={b.id} value={b.name} />)}</datalist>
        <datalist id="vehicle-type-list">{["Tata Ace","Pickup","407","709","19ft","20ft","22ft","24ft","32ft SXL","32ft MXL","Container 20ft","Container 32ft","Trailer","Open Body","Half Body"].map((t)=><option key={t} value={t} />)}</datalist>
        {(form.vehicles || []).length === 0 ? (
          <div className="text-[11px] text-[#68665E] border border-dashed border-[#E5E3DB] px-4 py-3">No vehicles added. Click "+ Add Vehicle". One LR can carry multiple vehicles/drivers.</div>
        ) : (form.vehicles || []).map((v, i) => {
          const upd = (k, val) => { const arr = form.vehicles.slice(); arr[i] = { ...arr[i], [k]: val }; set("vehicles", arr); };
          const pickV = (name) => { const d = drivers.find((x) => x.name === name); const arr = form.vehicles.slice(); arr[i] = { ...arr[i], driver_name: name, driver_phone: d?.phone || arr[i].driver_phone }; set("vehicles", arr); };
          const pickBroker = (name) => {
            const b = brokers.find((x) => x.name === name);
            const arr = form.vehicles.slice();
            arr[i] = { ...arr[i], broker_name: name, broker_id: b?.id || "" };
            if (b) {
              if (b.phone && !arr[i].broker_phone) arr[i].broker_phone = b.phone;
              if (b.vehicle_number && !arr[i].vehicle_number) arr[i].vehicle_number = b.vehicle_number;
              if (b.driver_name && !arr[i].driver_name) arr[i].driver_name = b.driver_name;
              if (b.driver_phone && !arr[i].driver_phone) arr[i].driver_phone = b.driver_phone;
            }
            set("vehicles", arr);
          };
          return (
            <div key={v._uid ?? i} className="border border-[#E5E3DB] p-3 mb-2" data-testid={`lr-vehicle-row-${i}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-4"><label className="input-label">Vehicle No</label><input list="vehicle-list" className="input-field font-mono uppercase" value={v.vehicle_number} onChange={(e)=>upd("vehicle_number", e.target.value.toUpperCase())} data-testid={`lr-vehicle-no-${i}`} /></div>
              <div className="md:col-span-4"><label className="input-label">Driver Name</label><input list="driver-list" className="input-field" value={v.driver_name} onChange={(e)=>pickV(e.target.value)} data-testid={`lr-vehicle-driver-${i}`} /></div>
              <div className="md:col-span-4"><label className="input-label">Driver Phone</label><input className="input-field" value={v.driver_phone} onChange={(e)=>upd("driver_phone", e.target.value)} data-testid={`lr-vehicle-phone-${i}`} /></div>
              <div className="md:col-span-4"><label className="input-label">Broker <span className="text-[10px] text-[#A09E96]">(internal only)</span></label><input list="broker-list" className="input-field" value={v.broker_name || ""} onChange={(e)=>pickBroker(e.target.value)} placeholder="Select or type broker" data-testid={`lr-vehicle-broker-${i}`} /></div>
              <div className="md:col-span-2"><label className="input-label">Broker Phone</label><input className="input-field" value={v.broker_phone || ""} onChange={(e)=>upd("broker_phone", e.target.value)} placeholder="Broker's number" data-testid={`lr-vehicle-broker-phone-${i}`} /></div>
              <div className="md:col-span-3"><label className="input-label">Vehicle Type</label><input list="vehicle-type-list" className="input-field" value={v.vehicle_type || ""} onChange={(e)=>upd("vehicle_type", e.target.value)} placeholder="e.g. 32ft SXL" data-testid={`lr-vehicle-type-${i}`} /></div>
              <div className="md:col-span-3"><label className="input-label">Dimensions</label><input className="input-field" value={v.dimensions || ""} onChange={(e)=>upd("dimensions", e.target.value)} placeholder="e.g. 20x8x8 ft" data-testid={`lr-vehicle-dims-${i}`} /></div>
              <div className="md:col-span-2"><label className="input-label">LH Advance</label><input type="number" className="input-field" value={v.lh_advance || 0} onChange={(e)=>upd("lh_advance", Number(e.target.value))} data-testid={`lr-vehicle-advance-${i}`} /></div>
              <div className="md:col-span-2"><label className="input-label">LH Balance</label><input type="number" className="input-field" value={v.lh_balance || 0} onChange={(e)=>upd("lh_balance", Number(e.target.value))} data-testid={`lr-vehicle-balance-${i}`} /></div>
              <div className="md:col-span-1"><label className="input-label">LH Total</label><div className="input-field bg-[#F5F4F0] font-medium" data-testid={`lr-vehicle-lh-${i}`}>{(Number(v.lh_advance||0)+Number(v.lh_balance||0)).toLocaleString("en-IN")}</div></div>
              <div className="md:col-span-1 flex items-end"><button type="button" className="btn-ghost text-[#8A2526]" onClick={()=>set("vehicles", form.vehicles.filter((_,idx)=>idx!==i))} data-testid={`lr-vehicle-remove-${i}`}>×</button></div>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-[#68665E]">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!v.lh_advance_paid} onChange={(e)=>upd("lh_advance_paid", e.target.checked)} data-testid={`lr-vehicle-advance-paid-${i}`} />
                  Advance Paid
                </label>
                <label className={`flex items-center gap-1.5 ${isDelivered ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}>
                  <input type="checkbox" disabled={!isDelivered} checked={isDelivered && !!v.lh_balance_paid}
                    onChange={(e)=>{ const arr=form.vehicles.slice(); arr[i]={...arr[i], lh_balance_paid:e.target.checked, lh_paid_date: e.target.checked ? (arr[i].lh_paid_date || new Date().toISOString().slice(0,10)) : ""}; set("vehicles", arr); }}
                    data-testid={`lr-vehicle-balance-paid-${i}`} />
                  Balance Paid
                </label>
                {!isDelivered && <span className="text-[#A0522D] font-medium" data-testid={`lr-vehicle-balance-locked-${i}`}>Balance not paid — pending delivery</span>}
                {isDelivered && v.lh_balance_paid && (
                  <span className="flex items-center gap-1">Paid on <input type="date" className="input-field !h-7 !py-0 !w-36" value={v.lh_paid_date || ""} onChange={(e)=>upd("lh_paid_date", e.target.value)} data-testid={`lr-vehicle-paid-date-${i}`} /></span>
                )}
              </div>
            </div>
          );
        })}
        {(form.vehicles || []).length > 0 && (
          <div className="flex justify-end mt-2 text-sm font-semibold text-[#1A1A18]" data-testid="lr-lh-total">
            Total Lorry Hire: ₹{(form.vehicles || []).reduce((s, v) => s + Number(v.lh_advance || 0) + Number(v.lh_balance || 0), 0).toLocaleString("en-IN")}
          </div>
        )}
      </div>

      <div>
        <div className="section-title">Sub-Transporter <span className="text-[11px] font-normal text-[#68665E]">(internal records only — not printed on the LR)</span></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="input-label">Sub-Transporter</label>
            <select className="input-field" value={st.id || ""} data-testid="lr-sub-select"
              onChange={(e)=>{ const s=subs.find((x)=>x.id===e.target.value); setSub({ id: e.target.value, name: s?.name || "", hub_name: "", poc_name: "", poc_phone: "" }); }}>
              <option value="">— None —</option>
              {subs.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          {selectedSub && (selectedSub.hubs || []).length > 0 && (
            <div>
              <label className="input-label">Hub</label>
              <select className="input-field" value={st.hub_name || ""} data-testid="lr-sub-hub"
                onChange={(e)=>setSub({ hub_name: e.target.value, poc_name: "", poc_phone: "" })}>
                <option value="">— Select hub —</option>
                {(selectedSub.hubs || []).map((h, hi)=><option key={h.hub_name || hi} value={h.hub_name}>{h.hub_name}{h.location?` (${h.location})`:""}</option>)}
              </select>
            </div>
          )}
          {selectedHub && (selectedHub.pocs || []).length > 0 && (
            <div>
              <label className="input-label">POC</label>
              <select className="input-field" value={st.poc_name || ""} data-testid="lr-sub-poc"
                onChange={(e)=>{ const p=(selectedHub.pocs||[]).find((x)=>x.name===e.target.value); setSub({ poc_name: e.target.value, poc_phone: p?.phone || "" }); }}>
                <option value="">— Select POC —</option>
                {(selectedHub.pocs || []).map((p, pi)=><option key={p.name || pi} value={p.name}>{p.name}{p.role?` — ${p.role}`:""}</option>)}
              </select>
            </div>
          )}
          {st.poc_name && <div><label className="input-label">POC Phone</label><input className="input-field" value={st.poc_phone || ""} onChange={(e)=>setSub({ poc_phone: e.target.value })} data-testid="lr-sub-poc-phone" /></div>}
          <div className="md:col-span-4"><label className="input-label">Notes</label><input className="input-field" value={st.notes || ""} onChange={(e)=>setSub({ notes: e.target.value })} placeholder="Internal note about this sub-transporter for this LR" data-testid="lr-sub-notes" /></div>
        </div>
      </div>

      <div>
        <div className="section-title">Proof of Delivery (Optional)</div>
        <div className="flex items-center gap-4">
          <label className="btn-secondary cursor-pointer w-fit">
            <Upload className="w-4 h-4" />
            {form.pod_image ? "Replace POD photo" : "Upload POD photo"}
            <input type="file" accept="image/*" className="hidden" onChange={onPodFile} data-testid="lr-pod-input" />
          </label>
          {form.pod_image && (<img src={form.pod_image} alt="POD" className="w-20 h-20 object-cover border border-[#E5E3DB]" />)}
        </div>
      </div>
    </>
  );
}
